import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import app from "./firebase";
import LoadingComponent from "./LoadingComponent";
import TranslateDropdown, { calcCredits } from "./components/TranslateDropdown";
import { useAuth } from "./context/AuthContext";

export default function PptPage() {
  const [pptFile, setPptFile] = useState(null);
  const [format, setFormat] = useState("qna");
  const [translateTo, setTranslateTo] = useState("");
  const [numQuestions, setNumQuestions] = useState(50);
  const [loading, setLoading] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:5000" : "");
  const navigate = useNavigate();
  const { credits, currentUser, refreshCredits } = useAuth();

  // Use the same credit calculation as other pages
  const creditCost = calcCredits("ppt", format, translateTo, numQuestions);

  // DEBUG: console.log("[PptPage] Render:", { credits, hasUser: !!currentUser });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.name.endsWith(".ppt") || file.name.endsWith(".pptx"))) {
      setPptFile(file);
    } else {
      alert("Please upload a valid PPT or PPTX file!");
      setPptFile(null);
    }
  };

  const handleFormatChange = (e) => setFormat(e.target.value);

  const handlePptProcess = async () => {
    if (!pptFile) return alert("Please upload a PPT/PPTX file first!");

    setLoading(true);

    try {
      const auth = getAuth(app);
      const user = auth.currentUser;

      if (!user) { alert("Please log in to use this feature"); setLoading(false); return; }

      const token = await user.getIdToken();

      const fileBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(pptFile);
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
      });

      const response = await fetch(`${API_BASE_URL}/api/process-ppt`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          fileData: fileBase64,
          fileName: pptFile.name,
          format,
          language: "en",
          translateTo,
          numQuestions
        }),
      });

      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        throw new Error(`Server error (${response.status}). The backend may be overloaded. Please try again.`);
      }

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.error || data.message || "Processing failed";
        throw new Error(errorMessage);
      }

      if (!data.transcript) { throw new Error("No transcript/notes received from server"); }

      if (refreshCredits) refreshCredits();

      navigate("/preview", {
        state: {
          transcript: data.transcript,
          qna_result: data.qna_result || [],
          paragraph_result: data.paragraph_result || "",
          mcq_result: data.mcq_result || [],
          fileName: pptFile.name,
          format: format,
        },
      });
    } catch (error) {
      console.error("Error:", error);
      alert(`Something went wrong: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ textAlign: "center", marginTop: "30px" }}>
      <h1>📊 PPT to Notes Generator</h1>

      {!loading ? (
        <div className="glass-container" style={{ maxWidth: "600px", margin: "0 auto" }}>

          {/* Force render if we have a user, show status if credits are missing */}
          {currentUser ? (
            <div id="wallet-balance-display" style={{
              background: 'linear-gradient(135deg, rgba(108, 92, 231, 0.2), rgba(0, 206, 201, 0.2))',
              border: '1px solid var(--primary-color)',
              borderRadius: '16px',
              padding: '1.5rem',
              marginBottom: '2rem',
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#fff',
              boxShadow: '0 0 20px rgba(108,92,231,0.1)'
            }}>
              {credits !== null ? (
                <>💰 Wallet Balance: <span style={{ color: 'var(--secondary-color)', fontSize: '1.2rem' }}>{credits}</span> Credits</>
              ) : (
                <>💰 Wallet Balance: <span style={{ color: 'orange' }}>Loading...</span></>
              )}
            </div>
          ) : (
            <p style={{ color: "orange" }}>Please log in to view your balance.</p>
          )}

          <div style={{ marginBottom: "20px" }}>
            <label htmlFor="ppt-file">Upload PPT/PPTX File:</label>
            <input
              id="ppt-file"
              type="file"
              accept=".ppt,.pptx"
              onChange={handleFileChange}
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label>Output Format:</label>
            <div className="radio-group" style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <input type="radio" value="qna" checked={format === "qna"} onChange={handleFormatChange} />
                Q&A (10 Comprehensive Pairs)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <input type="radio" value="paragraph" checked={format === "paragraph"} onChange={handleFormatChange} />
                Paragraph (Large)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <input type="radio" value="mcq" checked={format === "mcq"} onChange={handleFormatChange} />
                MCQ
              </label>
            </div>
            {format === "mcq" && (
              <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem' }}>
                  Number of Questions: <strong>{numQuestions}</strong>
                </label>
                <input
                  type="range"
                  min="10"
                  max="200"
                  step="10"
                  value={numQuestions}
                  onChange={(e) => setNumQuestions(parseInt(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--primary-color)' }}
                />
                <p style={{ fontSize: '0.75rem', color: numQuestions > 100 ? 'orange' : 'var(--text-muted)', marginTop: '5px' }}>
                  * Base is 200 credits up to 100 Qs.
                  {numQuestions > 100 && ` Surcharge: +${numQuestions - 100} extra credits.`}
                </p>
              </div>
            )}
          </div>

          {/* Translation temporarily disabled for testing */}


          <button
            className="btn-primary"
            onClick={handlePptProcess}
            disabled={!pptFile}
            style={{ width: "100%", background: "linear-gradient(45deg, var(--primary-color), var(--secondary-color))" }}
          >
            Process PPT — {creditCost} Credits
          </button>
        </div>
      ) : (
        <LoadingComponent />
      )}
    </div>
  );
}
